/**
 * Account Plans Page
 * Change subscription plan - upgrade/downgrade
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Info, Loader2 } from 'lucide-react';
import AccountHeader from '../../components/account/AccountHeader';
import PlanCard from '../../components/account/PlanCard';
import { useAccountAuth } from '../../contexts/AccountAuthContext';
import { API_BASE_URL } from '../../config/api';

const AccountPlans = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { subscription } = useAccountAuth();
  
  // Get category from navigation state or sessionStorage
  const category = location.state?.category || sessionStorage.getItem('webview_category') || 'property_management';
  
  const [plans, setPlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [plansError, setPlansError] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [billingInterval, setBillingInterval] = useState('monthly');
  const isAnnual = billingInterval === 'annual';

  useEffect(() => {
    fetchPlans(category);
  }, [category]);

  const fetchPlans = async (cat) => {
    try {
      setLoadingPlans(true);
      const response = await fetch(`${API_BASE_URL}/general/getgeneral`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          type: 'plan',
          category: cat
        })
      });
      
      const result = await response.json();
      
      if (result.success) {
        // Map API response to website format with structured features
        const mappedPlans = result.data.plans.map(plan => {
          return {
            id: plan.pid,
            name: plan.pn,
            description: plan.pd || '',
            price: plan.pp,
            annualPrice: plan.ppa || null,
            billingCycle: 'monthly',
            pricePerProperty: plan.ppiu,
            maxProperties: plan.miu,
            maxLocations: plan.ml,
            trialDays: plan.td || 0,
            displayOrder: plan.do || 0,
            inventoryType: plan.it || 'properties',
            displayFeatures: plan.df || [],
          };
        });
        setPlans(mappedPlans);
      } else {
        setPlansError(result.message);
      }
    } catch (err) {
      console.error('Error fetching plans:', err);
      setPlansError('Failed to load plans');
    } finally {
      setLoadingPlans(false);
    }
  };


  const handleSelectPlan = (plan) => {
    if (plan.id === subscription?.planId) return;
    // Navigate to checkout page with plan data
    navigate('/app/account/checkout', { state: { plan, billingInterval, fromPage: 'plans' } });
  };

  const handleConfirmChange = async () => {
    if (!selectedPlan) return;
    
    setIsLoading(true);
    try {
      // TODO: Replace with actual API call
      // POST /account/subscription/change { planId: selectedPlan.id }
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Mock success
      alert(`Plan changed to ${selectedPlan.name}! (Mock - API will be integrated later)`);
      setShowConfirm(false);
      navigate('/app/account/dashboard');
    } catch (error) {
      console.error('Error changing plan:', error);
      alert('Failed to change plan. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const currentPlanPrice = plans.find(p => p.id === subscription?.planId)?.price || 0;
  const isUpgrade = selectedPlan && selectedPlan.price > currentPlanPrice;

  // Determine recommended plan: next plan above current by display order
  const currentPlanOrder = plans.find(p => p.id === subscription?.planId)?.displayOrder ?? -1;
  const recommendedPlanId = (() => {
    const sorted = [...plans].sort((a, b) => a.displayOrder - b.displayOrder);
    const next = sorted.find(p => p.displayOrder > currentPlanOrder);
    return next?.id || null;
  })();

  // Loading state
  if (loadingPlans) {
    return (
      <div className="min-h-screen bg-dark">
        <AccountHeader title="Change Plan" />
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 text-accent animate-spin" />
        </div>
      </div>
    );
  }

  // Error state
  if (plansError) {
    return (
      <div className="min-h-screen bg-dark">
        <AccountHeader title="Change Plan" />
        <div className="flex items-center justify-center py-20">
          <p className="text-gray-400">{plansError}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dark">
      <AccountHeader title="Change Plan" />
      
      <div className="max-w-2xl mx-auto px-4 py-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <h2 className="text-2xl font-bold text-white mb-2">Simple, flat-rate pricing.</h2>
          <p className="text-gray-400 mb-5">Pick the plan that fits your portfolio.</p>

          {/* Billing Interval Toggle */}
          <div className="flex items-center justify-center gap-3">
            <span className={`text-sm transition-colors ${!isAnnual ? 'text-white font-semibold' : 'text-gray-500'}`}>
              Monthly
            </span>
            <button
              onClick={() => setBillingInterval(isAnnual ? 'monthly' : 'annual')}
              className={`relative w-11 h-6 rounded-full transition-colors ${isAnnual ? 'bg-accent' : 'bg-white/20'}`}
            >
              <span
                className={`absolute top-[3px] w-[18px] h-[18px] bg-white rounded-full transition-all shadow-sm ${isAnnual ? 'left-[23px]' : 'left-[3px]'}`}
              />
            </button>
            <span className={`text-sm transition-colors ${isAnnual ? 'text-white font-semibold' : 'text-gray-500'}`}>
              Annual{' '}
              <span className="text-xs font-bold text-accent">save 18%</span>
            </span>
          </div>
        </motion.div>

        {/* Plans */}
        <div className="space-y-4 mb-6">
          {plans.map((plan, index) => (
            <motion.div
              key={plan.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <PlanCard
                plan={plan}
                isCurrent={plan.id === subscription?.planId}
                isRecommended={plan.id === recommendedPlanId}
                onSelect={handleSelectPlan}
                isLoading={isLoading && selectedPlan?.id === plan.id}
                billingInterval={billingInterval}
              />
            </motion.div>
          ))}
        </div>

        {/* Info Note */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-xl"
        >
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-400 mt-0.5 shrink-0" />
            <div>
              <p className="text-sm text-blue-400 font-medium">Plan Change Info</p>
              <p className="text-xs text-blue-400/70 mt-1">
                Upgrades take effect immediately and are prorated. Downgrades take effect at the start of your next billing cycle.
              </p>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Confirmation Modal */}
      {showConfirm && selectedPlan && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="w-full max-w-md bg-dark-100 rounded-2xl p-6 border border-white/10"
          >
            <h3 className="text-xl font-bold text-white mb-2">
              {isUpgrade ? 'Upgrade' : 'Downgrade'} to {selectedPlan.name}?
            </h3>
            <p className="text-gray-400 mb-6">
              {isUpgrade 
                ? `You'll be charged $${isAnnual && selectedPlan.annualPrice ? (selectedPlan.annualPrice * 12) : selectedPlan.price}/${isAnnual ? 'year' : 'month'} starting today. The difference will be prorated.`
                : `Your plan will be changed to ${selectedPlan.name} at the start of your next billing cycle.`
              }
            </p>

            <div className="p-4 bg-dark-200 rounded-xl mb-6">
              <div className="flex justify-between items-center">
                <span className="text-gray-400">New Plan</span>
                <span className="text-white font-semibold">{selectedPlan.name}</span>
              </div>
              <div className="flex justify-between items-center mt-2">
                <span className="text-gray-400">New Price</span>
                <span className="text-white font-semibold">
                  ${isAnnual && selectedPlan.annualPrice ? selectedPlan.annualPrice : selectedPlan.price}/month
                  {isAnnual && <span className="text-xs text-gray-500 ml-1">(billed annually)</span>}
                </span>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowConfirm(false)}
                disabled={isLoading}
                className="flex-1 py-3 border border-white/20 text-white rounded-xl hover:bg-white/5 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmChange}
                disabled={isLoading}
                className="flex-1 py-3 bg-gradient-to-r from-accent to-accent-dark text-dark font-semibold rounded-xl hover:shadow-lg hover:shadow-accent/30 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    Processing...
                  </>
                ) : (
                  'Confirm Change'
                )}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default AccountPlans;
